/**
 * AcceptInvitePage — public page consumed via the emailed invitation link (#568).
 *
 * Flow:
 *   1. Org admin issues `POST /api/organizations/invites` (email + role —
 *      the admin never chooses a password).
 *   2. The invitation email is relayed via the control-plane Email Broker;
 *      its link is `${instanceUrl}/join?code=...` (control-plane template).
 *   3. Invitee lands here; `GET /api/organizations/invites/check/{code}`
 *      shows who invited them and the bound email — the email is READ-ONLY,
 *      it comes from the invite RECORD and cannot be changed here.
 *   4. Invitee sets their own name + password →
 *      `POST /api/organizations/invites/accept` (public, rate-limited;
 *      the body carries NO email field — the account binds to the record).
 *   5. On success → redirect to /login with toast.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { API as API_BASE } from '../../lib/api';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, CheckCircle, XCircle, Loader2, Eye, EyeOff, Mail } from 'lucide-react';

const ROLE_LABELS = { member: 'Member', org_admin: 'Org Admin' };

/** 403 seat-limit / plan errors arrive as an object detail; strings elsewhere. */
function errDetail(err, fallback) {
    const d = err?.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (d && typeof d === 'object') return d.message || fallback;
    return err?.message || fallback;
}

export default function AcceptInvitePage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const code = searchParams.get('code');

    const [checking, setChecking] = useState(true);
    const [invite, setInvite] = useState(null); // {org_name, email, role, invited_by_name}
    const [checkError, setCheckError] = useState('');

    const [name, setName] = useState('');
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [show, setShow] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [status, setStatus] = useState('checking'); // checking | ready | success | error
    const [message, setMessage] = useState('');
    const redirectTimer = useRef(null);

    // Clear the post-success redirect if the component unmounts first
    // (e.g. the invitee clicks "Sign In" before the 2s timer fires).
    useEffect(() => () => { if (redirectTimer.current) clearTimeout(redirectTimer.current); }, []);

    useEffect(() => {
        if (!code) { setCheckError('This page only works when opened via the link in your invitation email.'); setStatus('error'); return; }
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(`${API_BASE}/organizations/invites/check/${encodeURIComponent(code)}`);
                if (!cancelled) {
                    setInvite(res.data);
                    setStatus('ready');
                }
            } catch (err) {
                if (!cancelled) {
                    const d = errDetail(err, 'This invite is no longer valid.');
                    setCheckError(d);
                    setStatus('error');
                }
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [code]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (password.length < 8) {
            toast.error('Password must be at least 8 characters');
            return;
        }
        if (password !== confirm) {
            toast.error('Passwords do not match');
            return;
        }
        setSubmitting(true);
        try {
            const res = await axios.post(`${API_BASE}/organizations/invites/accept`, {
                invite_code: code,
                name,
                password,
            });
            setStatus('success');
            setMessage(res.data?.message || 'Account created. You can now sign in.');
            toast.success('Account created');
            redirectTimer.current = setTimeout(() => navigate('/login'), 2000);
        } catch (err) {
            const d = errDetail(err, 'Failed to create your account');
            setStatus('error');
            setMessage(d);
            toast.error(d);
        } finally {
            setSubmitting(false);
        }
    };

    if (status === 'checking') {
        return (
            <PageShell>
                <Loader2 className="w-8 h-8 text-stone-400 mx-auto mb-4 animate-spin" />
                <p className="text-stone-500 text-sm">Checking your invitation…</p>
            </PageShell>
        );
    }

    if (status === 'error') {
        return (
            <PageShell>
                <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-stone-900 mb-2" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                    Invitation unavailable
                </h2>
                <p className="text-stone-500 text-sm mb-6" data-testid="accept-error-message">{message || checkError}</p>
                <Link to="/login" className="inline-block bg-stone-900 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-stone-800 transition-colors">
                    Back to Login
                </Link>
            </PageShell>
        );
    }

    if (status === 'success') {
        return (
            <PageShell>
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-stone-900 mb-2" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                    You're all set
                </h2>
                <p className="text-stone-500 text-sm mb-6" data-testid="accept-success-message">{message}</p>
                <Link to="/login" className="inline-block bg-stone-900 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-stone-800 transition-colors">
                    Sign In
                </Link>
            </PageShell>
        );
    }

    // status === 'ready'
    return (
        <PageShell>
            <h2 className="text-xl font-semibold text-stone-900 mb-1 text-center" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                Join {invite?.org_name || 'your team'}
            </h2>
            <p className="text-stone-500 text-sm mb-5 text-center" data-testid="accept-invited-by">
                {invite?.invited_by_name || 'A team member'} invited you{invite?.role ? ` as ${ROLE_LABELS[invite.role] || invite.role}` : ''}.
            </p>

            <div className="bg-stone-50 border border-stone-200 rounded-lg p-3 mb-4 text-sm" data-testid="accept-invite-email">
                <div className="flex items-center gap-2 text-stone-700">
                    <Mail className="w-4 h-4 text-stone-400" />
                    <span className="font-medium">{invite?.email}</span>
                </div>
                <p className="text-xs text-stone-400 mt-1">Your account will use this email — it can't be changed here.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                    <label className="block text-xs font-medium text-stone-700 mb-1" htmlFor="accept-name">Your name</label>
                    <input
                        id="accept-name"
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="How your teammates will see you"
                        className="w-full px-3 py-2.5 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-stone-900"
                        data-testid="accept-name"
                        autoComplete="name"
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-stone-700 mb-1" htmlFor="accept-password">Create password</label>
                    <div className="relative">
                        <input
                            id="accept-password"
                            type={show ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="At least 8 characters"
                            className="w-full px-3 py-2.5 pr-10 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-stone-900"
                            autoFocus
                            required
                            minLength={8}
                            data-testid="accept-password"
                        />
                        <button
                            type="button"
                            onClick={() => setShow(s => !s)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-stone-400 hover:text-stone-700"
                            tabIndex={-1}
                        >
                            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                    </div>
                </div>
                <div>
                    <label className="block text-xs font-medium text-stone-700 mb-1" htmlFor="accept-confirm">Confirm password</label>
                    <input
                        id="accept-confirm"
                        type={show ? 'text' : 'password'}
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        placeholder="Re-enter your password"
                        className="w-full px-3 py-2.5 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-stone-900"
                        required
                        minLength={8}
                        data-testid="accept-confirm"
                    />
                </div>
                <button
                    type="submit"
                    disabled={submitting || !password || !confirm}
                    className="w-full bg-stone-900 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-stone-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    data-testid="accept-submit"
                >
                    {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                    {submitting ? 'Creating your account…' : 'Create account'}
                </button>
            </form>

            <p className="text-center text-xs text-stone-400 mt-6">
                Trouble with this link? Ask your admin to resend the invitation.
            </p>
        </PageShell>
    );
}

function PageShell({ children }) {
    return (
        <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#FAFAF5' }}>
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-14 h-14 bg-stone-900 rounded-2xl shadow-lg mb-4">
                        <Sparkles className="w-7 h-7 text-white" />
                    </div>
                    <h1 className="text-3xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                        Nurture <span className="text-stone-500">CRM</span>
                    </h1>
                </div>
                <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-8">
                    {children}
                </div>
            </div>
        </div>
    );
}
